import * as api from "./api";
import * as urls from "./urls";

import { ApiError, Duel } from "./api";
import { Board, generateRandomBoard, getAllWords } from "./board";
import { GAME, GameStateMarker } from "./game-state";
import { rescoreList, scoreWordBase } from "./scoring";
import { isWordValid } from "./utils";

export const GameController = {
  startGame(): void {
    urls.setHomeUrl();

    const fromMarker = GAME.state.marker;
    const toMarker = GameStateMarker.InProgress;
    if (fromMarker === GameStateMarker.NotStarted) {
      GAME.state = {
        marker: toMarker,
        board: GAME.state.board,
        userWordList: [],
        userWordSet: new Set(),
        isPaused: false,
        duel: null,
        duelNickname: "",
      };
    } else if (
      fromMarker === GameStateMarker.Finished ||
      fromMarker === GameStateMarker.DuelResults
    ) {
      GAME.state = {
        marker: toMarker,
        board: generateRandomBoard(),
        userWordList: [],
        userWordSet: new Set(),
        isPaused: false,
        duel: null,
        duelNickname: "",
      };
    } else {
      GameController.illegalStateTransition(fromMarker, toMarker);
    }
  },

  startDuel(duel: Duel, nickname: string): void {
    const fromMarker = GAME.state.marker;
    const toMarker = GameStateMarker.InProgress;
    if (fromMarker === GameStateMarker.NotStarted) {
      GAME.state = {
        marker: toMarker,
        board: new Board(duel.boardLetters),
        userWordList: [],
        userWordSet: new Set(),
        isPaused: false,
        duel,
        duelNickname: nickname,
      };
    } else if (fromMarker === GameStateMarker.DuelChallenge2) {
      GAME.state = {
        marker: toMarker,
        board: new Board(duel.boardLetters),
        userWordList: [],
        userWordSet: new Set(),
        isPaused: false,
        duel,
        duelNickname: nickname,
      };
    } else {
      GameController.illegalStateTransition(fromMarker, toMarker);
    }
  },

  onToggleTimer(): void {
    if (GAME.state.marker === GameStateMarker.InProgress) {
      GAME.state.isPaused = !GAME.state.isPaused;
    } else {
      throw new Error(
        `unexpected onToggleTimer() when in state ${GAME.state.marker}`
      );
    }
  },

  onInput(value: string): void {
    if (GAME.state.marker === GameStateMarker.InProgress) {
      GAME.state.board.clearHighlighted();
      for (const letter of value.toUpperCase()) {
        const success = GAME.state.board.addToHighlight(letter);
        if (!success) {
          break;
        }
      }
    } else {
      throw new Error(
        `unexpected onInput() when in state ${GAME.state.marker}`
      );
    }
  },

  onEnter(): number {
    if (GAME.state.marker === GameStateMarker.InProgress) {
      const board = GAME.state.board;
      if (board.paths.length === 0) {
        return 0;
      }

      const wordAsArray = [];
      for (const xy of board.paths[0]) {
        const [x, y] = xy;
        wordAsArray.push(board.at(x, y));
      }
      const word = wordAsArray.join("");
      board.clearHighlighted();

      if (isWordValid(word) && !GAME.state.userWordSet.has(word)) {
        const score = rescoreList(word, GAME.state.userWordList);
        GAME.state.userWordList.unshift([word, score]);
        GAME.state.userWordSet.add(word);
        return score;
      } else {
        return 0;
      }
    } else {
      throw new Error(
        `unexpected onEnter() when in state ${GAME.state.marker}`
      );
    }
  },

  finishGame(): void {
    const fromMarker = GAME.state.marker;
    const toMarker = GameStateMarker.Finished;
    if (fromMarker === GameStateMarker.InProgress) {
      const userWordList = GAME.state.userWordList;
      const score = userWordList.reduce(
        (acc: number, wordScorePair: [string, number]) =>
          acc + wordScorePair[1],
        0
      );
      const missedWords = getMissedWords(
        GAME.state.userWordSet,
        GAME.state.board
      );

      let maxScore = score;
      for (const word of missedWords) {
        maxScore += scoreWordBase(word);
      }

      if (GAME.state.duel) {
        const board = GAME.state.board;
        const nickname = GAME.state.duelNickname;
        const words = userWordList.map((pair) => pair[0]);
        api
          .saveDuel(
            GAME.state.duel.duelId,
            GAME.state.duelNickname,
            score,
            words
          )
          .then((savedDuel: Duel) => {
            GAME.state = {
              marker: toMarker,
              board,
              score,
              maxScore,
              userWordList,
              missedWords,
              duel: savedDuel,
              duelNickname: nickname,
            };

            urls.setResultsUrl(savedDuel.duelId);
          })
          .catch((error: ApiError) => {
            GAME.state = {
              marker: toMarker,
              board,
              score,
              maxScore,
              userWordList,
              missedWords,
              duel: null,
              duelNickname: "",
              error: {
                message: "failed to save duel",
                code: error.code,
              },
            };
          });
      } else {
        GAME.state = {
          marker: toMarker,
          board: GAME.state.board,
          score,
          maxScore,
          userWordList,
          missedWords,
          duel: null,
          duelNickname: "",
        };
      }
    } else {
      GameController.illegalStateTransition(fromMarker, toMarker);
    }
  },

  acceptDuel(): void {
    const fromMarker = GAME.state.marker;
    const toMarker = GameStateMarker.DuelChallenge2;
    if (fromMarker === GameStateMarker.DuelChallenge1) {
      GAME.state = {
        marker: toMarker,
        duel: GAME.state.duel,
        nickname: "",
      };
    } else {
      GameController.illegalStateTransition(fromMarker, toMarker);
    }
  },

  illegalStateTransition(
    fromMarker: GameStateMarker,
    toMarker: GameStateMarker
  ): never {
    throw new Error(
      `cannot transition from state ${fromMarker} to state ${toMarker}`
    );
  },
};

export function getMissedWords(userWordSet: Set<string>, board: Board): string[] {
  const missedWords = [];
  for (const word of getAllWords(board)) {
    if (!userWordSet.has(word)) {
      missedWords.push(word);
    }
  }
  missedWords.sort((a, b) => {
    // First, if the words are of different lengths, put the longer word
    // first.
    if (a.length !== b.length) {
      return b.length - a.length;
    }

    // If they are the same length, put higher-scoring words first.
    const scoreA = scoreWordBase(a);
    const scoreB = scoreWordBase(b);
    if (scoreA !== scoreB) {
      return scoreWordBase(b) - scoreWordBase(a);
    }

    // Finally, words of the same score and length are sorted alphabetically.
    return a.localeCompare(b);
  });
  return missedWords;
}
